import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise"; // Import Ag-Grid Enterprise features
import "ag-grid-enterprise/styles/ag-grid.css";
import "ag-grid-enterprise/styles/ag-theme-quartz.css";
import "../AgTable.scss";
import FinkraftLogo from "static/images/FinLogo.png";
// import "../AgTheme.css"
import { Button, Empty, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { handleAgCellDataFormat, handleCellRenderer } from "../AgUtility";
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PDFViewerTool from "../HelperTools/PDFViewerTool";
import PDFViewerToolV2 from "../HelperTools/PDFViewerTool/pdfViewerV2";
import {
  API_ENDPOINT_AG_TABLE_LOAD_VIEW,
  API_ENDPOINT_AG_TABLE_SAVE_VIEW,
  API_ENDPOINT_AG_TABLE_UPDATE_VIEW,
} from "../AgTable.constants";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  RightCircleOutlined,
} from "@ant-design/icons";
import { apiGet, apiPost, apiPut } from "app/services/apiServices";
import Loader from "app/shared/Loader";
import CRMTransfers from "app/shared/CRMTRansfer";
import { width } from "@mui/system";
import moment from "moment";
import { useRecoilState } from "recoil";
import { userInfo } from "app/config/States/users";
import AgCustomHeader from "app/scenes/Credentials/components/TrialAG/CustomColumn";

const detailCellRendererParams = {
  detailGridOptions: {
    columnDefs: [{ field: "price", headerName: "Price" }],
  },
  getDetailRowData: (params) => {
    params.successCallback(params.data.details);
  },
};

const NoRowsOverlay = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
    <Empty description="No Data Available" />
  </div>
);

const AgTableClient = forwardRef(function (props, ref) {
  // Column definitions

  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setGridColumnApi] = useState(null);
  const [messageApi, contextHolder] = message.useMessage();
  const [loadingView, setLoadingView] = useState(false);
  const [initialColumnState, setInitialColumnState] = useState([]);
  const [userDetails, setUserDetails] = useRecoilState(userInfo);
  const [chartCurrentRef, setCurrentChartRef] = useState(null);
  const gridRef = useRef();
  const processColumnDefs = (columnDefs) => {
    if (!Array.isArray(columnDefs)) {
      console.warn("AgTableClient: expected columnDefs array, received:", columnDefs);
      return [];
    }
    try {
      const newDefs = columnDefs.map((obj) => ({
        ...obj,
        ...(obj && obj.formatType ? handleCellRenderer() : {}),
        ...(obj && obj.editRow
          ? {
            editFunction: (params, status, colKey) =>
              handleCellEditing(params, status, colKey),
          }
          : {}),
        ...(obj && obj.children ? { children: processColumnDefs(obj.children) } : {}),
      }));
      return newDefs;
    } catch (e) {
      return Array.isArray(columnDefs) ? columnDefs : [];
    }
  };
  const [useServer, setUseServer] = useState(false);

  useEffect(() => {
    if (gridApi && gridColumnApi) {
      props.autoResize && gridApi.sizeColumnsToFit();
    }
  }, [props.columnDefs, gridApi, gridColumnApi]);

  const onGridReady = useCallback(async (params) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);

    params.api.autoSizeAllColumns();

    if (!props.rowData || props.rowData.length === 0) {
      setTimeout(() => params.api.showNoRowsOverlay(), 0);
    }
  }, [props.rowData]);

  const onCellValueChanged = useCallback((event) => {
    props.onCellValueChanged && props.onCellValueChanged(event);
  }, [props.onCellValueChanged]);

  const onRowValueChanged = useCallback((event) => {
    props.onRowSaved && props.onRowSaved(event.data);
  }, []);

  const handleCellEditing = (params, editingOn, colKey) => {
    if (!editingOn) {
      onBtStopEditing();
    } else {
      onBtStartEditing(params, colKey);
    }
  };
  const onBtStartEditing = useCallback((params, colKey) => {
    gridRef.current.api.setFocusedCell(params?.rowIndex, colKey);
    gridRef.current.api.startEditingCell({
      rowIndex: params?.rowIndex,
      colKey: colKey,
    });
  }, []);

  const onBtStopEditing = useCallback(() => {
    gridRef.current.api.stopEditing();
  }, []);

  const defaultExcelExportParams = useMemo(() => {
    return {
      // autoConvertFormulas: true,
      // exportAsExcelTable: true,
      processCellCallback: (params) => {
        const field = params.column.colDef;
        return field.cellClass === "hyperlinks"
          ? params.value
            ? `=HYPERLINK("${params.value}")`
            : ""
          : params.value;
      },
      rowHeight: (params) => (params.rowIndex === 1 ? 60 : 20),

      addImageToCell: (rowIndex, col, value) => {
        if (rowIndex !== 1) {
          return;
        }
        return {
          image: {
            id: "logo",
            base64: value,
            imageType: "png",
            width: 200,
            height: 200,
            position: {
              colSpan: 4,
            },
          },
        };
      },
    };
  }, []);

  const processColumns = (columns, data) => {
    return columns.reduce((acc, colDef) => {
      if (colDef.children) {
        // Recursively process child columns
        Object.assign(acc, processColumns(colDef.children, data));
      } else {
        const field = colDef.field;
        const headerName = colDef.headerName;

        if (colDef.enableValue) {
          // Sum the values if the column has enableValue as true
          acc[headerName] = data.reduce(
            (sum, row) => sum + (row[field] || 0),
            0
          );
        } else {
          // Count the occurrences if the column has enableValue as false
          acc[headerName] = data.length;
        }
      }

      return acc;
    }, {});
  };

  const getSummaryObject = () => {
    const columnDefs = gridRef.current?.api?.getColumnDefs();

    if (gridRef.current && gridRef.current.api) {
      const filteredData = [];
      gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
        if (!node.group) {
          filteredData.push(node.data);
        }
      });

      const summaryObject = processColumns(columnDefs, filteredData);
      return summaryObject;
    }
  };

  const formatNumberWithCommas = (number) => {
    if (typeof number === "number") {
      return new Intl.NumberFormat().format(number);
    }
    return number;
  };

  const exportDataToExcel = useCallback(() => {
    // const performExport = async () => {
    //   let summaryObject = getSummaryObject();
    //   const spreadsheets = [];

    //   const columnDefs = gridRef.current?.api?.getColumnDefs();

    //   let cellColumnData = [];
    //   columnDefs.forEach((colDef) => {
    //     if (colDef.children && colDef.children.length > 0) {
    //       colDef.children.forEach((childColDef) => {
    //         cellColumnData.push({
    //           cells: [
    //             {
    //               styleId: "headerName",
    //               data: {
    //                 value: childColDef.headerName,
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "",
    //               data: {
    //                 value: childColDef.enableValue ? "NUMBER" : "STRING",
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "",
    //               data: {
    //                 value: childColDef.enableValue ? "SUM" : "COUNT",
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "",
    //               data: {
    //                 value: colDef.rowGroup ? "YES" : "NO",
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "",
    //               data: {
    //                 value: colDef.aggFunc ? "YES" : "NO",
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "",
    //               data: {
    //                 value: colDef.aggFunc ? "YES" : "NO",
    //                 type: "String",
    //               },
    //             },
    //             {
    //               styleId: "numberFormat",
    //               data: {
    //                 value: summaryObject[childColDef.headerName],
    //                 type: "Number",
    //               },
    //             },
    //           ],
    //         });
    //       });
    //     } else {
    //       cellColumnData.push({
    //         cells: [
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.headerName,
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.enableValue ? "NUMBER" : "STRING",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.enableValue ? "SUM" : "COUNT",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.rowGroup ? "YES" : "NO",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.aggFunc ? "YES" : "NO",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "",
    //             data: {
    //               value: colDef.aggFunc ? "YES" : "NO",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "numberFormat",
    //             data: {
    //               value: summaryObject[colDef.headerName],
    //               type: "Number",
    //             },
    //           },
    //         ],
    //       });
    //     }
    //   });
    //   let FinkraftLogoBase64 =
    //     "data:image/png;base64,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";
    //   // First sheet (cover sheet) with custom content only
    //   const coverSheet = gridRef.current.api.getSheetDataForExcel({
    //     prependContent: [
    //       {
    //         cells: [
    //           {
    //             styleId: "",
    //             mergeAcross: 6,
    //             data: {
    //               type: "String",
    //               value: FinkraftLogoBase64, // see imageUtils
    //             },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [],
    //       },

    //       {
    //         cells: [
    //           {
    //             styleId: "coverHeading",
    //             mergeAcross: 6,
    //             data: { value: "Policy Bazar Recon Report", type: "String" },
    //             rowHeight: 30,
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             mergeAcross: 6,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: "Created By", type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: userDetails?.email, type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             mergeAcross: 4,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: "Email/Phone", type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: userDetails?.email, type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             mergeAcross: 4,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: "Created On", type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             data: { value: moment().format("DD/MM/YYYY"), type: "String" },
    //           },
    //           {
    //             styleId: "coverSubHeader",
    //             mergeAcross: 4,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             mergeAcross: 6,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             mergeAcross: 6,
    //             data: { value: "", type: "String" },
    //           },
    //         ],
    //       },
    //       {
    //         cells: [
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Column",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Data Type",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Aggregation Type",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Using Grouping",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Using Pivot",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Using Filter",
    //               type: "String",
    //             },
    //           },
    //           {
    //             styleId: "header",
    //             data: {
    //               value: "Aggregation Value",
    //               type: "String",
    //             },
    //           },
    //         ],
    //       },
    //       ...cellColumnData,
    //     ],
    //     columnWidth: (params) => {
    //       if (params.index === 0) {
    //         // Assuming you want to customize the first column
    //         return 400; // Custom width for the first column in pixels
    //       }
    //       return 150; // Default width for other columns
    //     },

    //     processHeaderCallback: () => "",
    //     processRowGroupCallback: () => "",
    //     processGroupHeaderCallback: () => "",
    //     processCellCallback: () => "",
    //     sheetName: "cover",
    //   });

    //   spreadsheets.push(coverSheet);

    //   gridRef.current.api.onFilterChanged();

    //   // Second sheet with actual grid data
    //   const dataSheet = gridRef.current.api.getSheetDataForExcel({
    //     sheetName: "data",
    //     processCellCallback: (params) => {
    //       if (typeof params.value === "number") {
    //         // Return just the number to ensure it stays as a number in Excel
    //         return params.value;
    //       }
    //       return params.value; // For non-numeric values, return as is
    //     },
    //   });

    //   spreadsheets.push(dataSheet);

    //   // Export combined sheets as an Excel file
    //   gridRef.current.api.exportMultipleSheetsAsExcel({
    //     data: spreadsheets,
    //     fileName: "ag-grid.xlsx",
    //   });
    // };
    // performExport();
    gridRef.current.api.exportDataAsExcel();
  }, [gridRef]);
  const exportDataToCSV = useCallback(() => {
    const params = {
      suppressQuotes: true, // Suppress quotes around cell values
      processHeaderCallback: (params) => {
        // Convert column headers to lowercase
        return params.column.getColDef().headerName.toLowerCase();
      },

      allColumns: true, // Include all columns in the export
      skipPinnedBottom: false, // Ensure pinned bottom rows (like grand total) are included

      processCellCallback: (params) => {
        // Convert cell values to lowercase if they are strings

        return params.value;
      },
    };
    gridRef.current.api.exportDataAsCsv(params);
  }, [gridRef]);

  const getGridState = () => {
    try {
      if (!gridRef.current) {
        return null;
      }
      const api = gridRef.current.api;
      if (!api) {
        return null;
      }
      // Safely get grid state with null checks
      const getSafeModel = (method) => {
        try {
          return typeof api[method] === 'function' ? api[method]() : {};
        } catch (e) {
          return {};
        }
      };
      const columnApi = gridRef.current.columnApi;
      const columnState = columnApi?.getColumnState ? columnApi.getColumnState() : [];
      // Get sort model from column state to ensure we have the most accurate sort information
      const sortModel = columnState
        .filter(col => col.sort)
        .map(col => ({
          colId: col.colId,
          sort: col.sort,
          sortIndex: col.sortIndex
        }));
      const gridState = {
        filterModel: getSafeModel('getFilterModel'),
        sortModel: sortModel.length ? sortModel : getSafeModel('getSortModel'),
        columnState: columnState,
        columnGroupState: columnApi?.getColumnGroupState ? columnApi.getColumnGroupState() : [],
        pivotMode: columnApi?.isPivotMode ? columnApi.isPivotMode() : false,
        groupKeys: getSafeModel('getGroupKeys'),
        rowGroupCols: columnApi?.getRowGroupColumns ? columnApi.getRowGroupColumns().map(c => ({
          id: c.getColId(),
          displayName: c.getColDef().headerName || c.getColId(),
          field: c.getColId()
        })) : [],
        valueCols: columnApi?.getValueColumns ? columnApi.getValueColumns().map(c => ({
          id: c.getColId(),
          displayName: c.getColDef().headerName || c.getColId(),
          field: c.getColId(),
          aggFunc: c.getColDef().aggFunc
        })) : [],
        pivotCols: columnApi?.getPivotColumns ? columnApi.getPivotColumns().map(c => ({
          id: c.getColId(),
          displayName: c.getColDef().headerName || c.getColId(),
          field: c.getColId()
        })) : [],
        pagination: {
          // pageSize: api.paginationGetPageSize ? api.paginationGetPageSize() : 100,
          // currentPage: api.paginationGetCurrentPage ? api.paginationGetCurrentPage() : 0,
          pageSize:  100,
          currentPage: 0,
        },
        timestamp: new Date().toISOString()
      };
      return gridState;
    } catch (error) {
      return null;
    }
  };
  const getServerSidePayload = () => {
    if (gridApi) {
      const filterModel = gridApi.getFilterModel
        ? gridApi.getFilterModel()
        : null;
      // const sortModel = gridApi.getSortModel ? gridApi.getSortModel() : null;
      const groupKeys = gridApi.getGroupKeys ? gridApi.getGroupKeys() : [];
      const pagination = gridApi.paginationGetPageSize
        ? gridApi.paginationGetPageSize()
        : 0;

      const rowGroupCols = gridApi.getRowGroupColumns
        ? gridApi.getRowGroupColumns().map((col) => ({
            id: col.getColId(),
            displayName: col.getColDef().headerName || col.getColId(),
            field: col.getColId()
          }))
        : [];
      const valueCols = gridApi.getValueColumns
        ? gridApi.getValueColumns().map((col) => ({
            id: col.getColId(),
            displayName: col.getColDef().headerName || col.getColId(),
            field: col.getColId(),
            aggFunc: col.getColDef().aggFunc
          }))
        : [];
      const pivotCols = gridApi.getPivotColumns
        ? gridApi.getPivotColumns().map((col) => ({
            id: col.getColId(),
            displayName: col.getColDef().headerName || col.getColId(),
            field: col.getColId()
          }))
        : [];

      const sortModel = gridApi
        .getColumnState()
        .filter((col) => col.sort) // Only columns that have sorting
        .map((col) => ({
          colId: col.colId,
          sort: col.sort, // 'asc' or 'desc'
        }));
      const serverSidePayload = {
        startRow: 0,
        endRow: 100,
        rowGroupCols,
        valueCols,
        pivotCols,
        pivotMode: gridApi.isPivotMode(),
        groupKeys,
        filterModel,
        sortModel,
      };

      return serverSidePayload;
    }
    return null;
  };

  const saveGridView = async (viewName, moduleId) => {
    const gridState = getGridState();

    // Clean up module ID by removing _0 or _1 suffix if present
    const cleanModuleId = typeof moduleId === 'string'
      ? moduleId.replace(/_\d+$/, '')
      : moduleId;

    const payload = {
      name: viewName,
      gridState: gridState,
      moduleId: cleanModuleId,
    };
    const response = await apiPost(API_ENDPOINT_AG_TABLE_SAVE_VIEW, payload);
    if (response.status) {
      messageApi.success({
        type: "success",
        content: "Awesome👍 , your view is created",
      });
    } else {
      messageApi.error({
        type: "error",
        content: "Could not save view, please try again",
      });
    }
  };

  const updateGridView = async (viewId, moduleId) => {
    const gridState = getGridState();
    const payload = {
      viewId: viewId,
      gridState: gridState,
      moduleId: moduleId,
    };
    const response = await apiPut(
      `${API_ENDPOINT_AG_TABLE_UPDATE_VIEW}${viewId}/update`,
      payload
    );
    if (response.status) {
      messageApi.success({
        type: "success",
        content: "Awesome👍 , your view is updated",
      });
    } else {
      messageApi.error({
        type: "error",
        content: "Could not save view, please try again",
      });
    }
  };

  const loadGridView = async (viewId, moduleId) => {
    const gridApi = gridRef.current?.api;
    const columnApi = gridRef.current?.columnApi;

    if (viewId === "DEFAULT") {
      if (gridApi && columnApi) {
        gridApi.resetColumnState();

        columnApi.applyColumnState({
          state: initialColumnState,
          applyOrder: false,
        });

        columnApi.setRowGroupColumns([]);
        columnApi.setPivotColumns([]);
        columnApi.setPivotMode(false);

        gridApi.setFilterModel({});
        gridApi.refreshClientSideRowModel();
      }
      setLoadingView(false);
      return;
    }

    const cleanModuleId =
      typeof moduleId === "string" ? moduleId.replace(/_\d+$/, "") : moduleId;

    const workspaceSegment = props.currentWorkspaceId
      ? `${props.currentWorkspaceId}`
      : "";

    const response = await apiGet(
      `${API_ENDPOINT_AG_TABLE_LOAD_VIEW}${cleanModuleId}/${viewId}/${workspaceSegment}`
    );

    if (response.status && response.data) {
      const view = response.data;

      if (columnApi && view?.state?.columnState && view?.state?.rowGroupCols) {
        columnApi.applyColumnState({
          state: view.state.columnState,
          applyOrder: true,
        });

        // Extract 'field' values from saved rowGroupCols objects
        const rowGroupColIds = view.state.rowGroupCols.map(col => col.field);

        // Set row groups
        columnApi.setRowGroupColumns(rowGroupColIds);

        // Restore pivot columns and mode
        columnApi.setPivotColumns(view.state.pivotCols || []);
        columnApi.setPivotMode(view.state.pivotMode || false);
      }

      if (gridApi) {
        // ✅ use filterModel
        gridApi.setFilterModel(view.state.filterModel || {});

        // ✅ use sortModel
        // (view.state.sortModel || []).forEach((sort) => {
        //   gridApi.getColumnState().forEach((column) => {
        //     if (column.colId === sort.colId) {
        //       column.sort = sort.sort;
        //     }
        //   });
        // });
        (Array.isArray(view?.state?.sortModel) ? view.state.sortModel : []).forEach((sort) => {
          gridApi.getColumnState().forEach((column) => {
            if (column.colId === sort.colId) {
              column.sort = sort.sort;
            }
          });
        });

        gridApi.refreshClientSideRowModel();
      }
    }
    setLoadingView(false);
  };




  const loadGridView2 = async (viewData) => {
    const gridApi = gridRef.current.api;
    const columnApi = gridRef.current.columnApi;
    setLoadingView(true);
    const chartContainers = document.querySelectorAll(".ag-popup");
    chartContainers.forEach((container) => container.remove());
    gridApi.refreshClientSideRowModel(); // This clears charts

    chartCurrentRef?.destroyChart();
    setCurrentChartRef(undefined);

    if (viewData === "DEFAULT") {
      if (columnApi) {
        gridApi.resetColumnState();

        columnApi.applyColumnState({
          state: initialColumnState,
          applyOrder: false,
        });
        setLoadingView(false);

        columnApi.setRowGroupColumns([]); // Set row group columns
        columnApi.setPivotColumns([]); // Set pivot columns
        columnApi.setPivotMode(false); // Set pivot mode
      }

      // Apply sort and filter states
      if (gridApi) {
        gridApi.setFilterModel({}); // Apply filter model

        gridApi.refreshClientSideRowModel(); // Refresh the grid to apply the sorting
      }

      return;
    }

    const request = viewData;

    // Set filters
    gridApi.setFilterModel(request.filterModel);

    // Set sorting
    if (request.sortModel) gridApi.setSortModel(request.sortModel);

    // Set grouping
    if (request.rowGroupCols.length > 0) {
      gridApi.setRowGroupColumns(request.rowGroupCols);
    }

    // Set pivoting
    if (request.pivotCols.length > 0) {
      columnApi.setPivotMode(request.pivotMode);
      columnApi.setPivotColumns(request.pivotCols);
    }
    columnApi.setPivotMode(request.pivotMode);

    setLoadingView(false);
  };

  const getScrollPosition = () => {
    return gridRef.current && gridRef.current.api.getVerticalPixelRange();
  };
  const setScrollPosition = (position) => {
    return gridRef.current && gridRef.current.api.ensureIndexVisible(position);
  };

  const applyTransaction = (data) => {
    return gridRef.current && gridRef.current.api.applyTransaction(data);
  };

  const getFinalDataFromTable = (data) => {
    const updatedData = [];
    gridRef.current.api.forEachNode((node) => {
      updatedData.push(node.data);
    });
    return updatedData;
  };

  React.useImperativeHandle(ref, () => ({
    api: gridRef.current?.api,
    exportDataToExcel,
    exportDataToCSV,
    exportJSONData,
    getAllColumns,
    onTableSearch,
    updateGridView,
    getGridState,
    saveGridView,
    loadGridView,
    getScrollPosition,
    getFinalDataFromTable,
    applyTransaction,
    setScrollPosition,
    getServerSidePayload,
    loadGridView2,
    setFilterModel: (filterModel) => {
      gridRef.current.api.setFilterModel(filterModel);
    },
    getChartModels: () => {
      return gridRef.current.api.getChartModels();
    },

    setChartModels: (chartModel) => {
      const gridApi = gridRef.current.api;
      const columnApi = gridRef.current.columnApi;
      const customContainer = document.createElement("div");
      customContainer.style.width = "80%"; // Set as percentage
      customContainer.style.height = "100%"; // Fixed or dynamic height
      document.body.appendChild(customContainer);

      gridApi.resetColumnState();

      columnApi.applyColumnState({
        state: initialColumnState,
        applyOrder: false,
      });
      setLoadingView(false);

      columnApi.setRowGroupColumns([]); // Set row group columns
      columnApi.setPivotColumns([]); // Set pivot columns
      columnApi.setPivotMode(false); // Set pivot mode
      let chartResponse = gridRef.current.api.restoreChart({
        ...chartModel,
        chartContainer: customContainer,
      });
      setCurrentChartRef(chartResponse);
    },
    destroyChart: () => {
      chartCurrentRef?.destroyChart();
      setCurrentChartRef(undefined);
    },
    setSortModel: (sortModel) => {
      if (gridRef.current) {
        // gridRef.current.api.setSortModel(sortModel);
        gridRef.current.setSortModel(sortModel)
      }
    },
    applyColumnState: (sortModel) => {
      if (gridRef.current) {
        gridRef.current.api.applyColumnState({
          state: sortModel.map((s) => ({ colId: s.colId, sort: s.sort })),
        });
      }
    },
    setGroupModel: (groupModel) => {
      gridRef.current.columnApi.setRowGroupColumns(
        groupModel.map((group) => group.colId || group.field)
      );
    },
  }));

  const exportJSONData = () => {
    if (gridApi) {
      const rowData = [];
      gridApi.forEachNode((node) => rowData.push(node.data));

      const transformedData = rowData.map((row) => {
        const obj = {};
        props.columnDefs.forEach((column) => {
          obj[column.field] = row[column.field];
        });
        return obj;
      });

      return transformedData;
    }
  };

  const handleColumnVisibilityChange = () => {
    if (!gridApi) return;
    const columnState = gridApi.getColumnState();
    const groupedColumn = columnState.find((column) => column.hide);
    props.onColGroup && props.onColGroup(groupedColumn);
    // setGroupedColumn(groupedColumn ? groupedColumn.colId : null);
  };

  const getAllColumns = () => {
    return gridRef.current.columnApi.getColumnState();
  };

  const statusBar = {
    statusPanels: [
      {
        statusPanel: "agTotalAndFilteredRowCountComponent",
        align: "left",
      },
      {
        statusPanel: "agTotalRowCountComponent",
        align: "center",
      },
      {
        statusPanel: "agFilteredRowCountComponent",
        align: "center",
      },
      {
        statusPanel: "agSelectedRowCountComponent",
        align: "right",
      },
      {
        statusPanel: "agAggregationComponent",
        align: "right",
      },
    ],
  };

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "pdfRenderer",
          labelDefault: "PDF View",
          labelKey: "pdfRenderer",
          iconKey: "pdf-file",
          width: 500,
          //isOpen: true,
          toolPanel: props.pdfV2 ? PDFViewerToolV2 : PDFViewerTool,
          toolPanelParams: {
            dataKey: props.pdfDataKey,
            pdfData: props.pdfData,
          },
        },
        {
          id: "crmTransfer",
          labelDefault: "CRM Transfer",
          labelKey: "crmTransfer",
          width: 500,
          toolPanel: CRMTransfers,
        },
      ],
    }),
    []
  );

  const onTableSearch = (event) => {
    gridRef.current && gridRef.current?.api?.setQuickFilter(event.target.value);
  };

  const getRowId = (params) => params.data?.[props.rowIdKey]; // Assuming 'id' is unique for each row

  const excelStyles = useMemo(() => {
    return [
      // {
      //   id: "numberFormat",
      //   numberFormat: {
      //     format: "#,##0",
      //   },
      // },
      // {
      //   id: "dateType",
      //   dataType: "DateTime",
      // },
      // {
      //   id: "dateUK",
      //   dataType: "DateTime",
      //   numberFormat: {
      //     format: "dd/mm/yy",
      //   },
      // },
      {
        id: "headerName",

        font: {
          bold: true,
        },
      },
      {
        id: "header",
        interior: {
          color: "#f5f6f9", // Gray background
          pattern: "Solid",
        },
        font: {
          bold: true,
          size: 12, // Font size
          color: "#283b5a", // Blue font color
        },
        alignment: {
          horizontal: "Center",
          vertical: "Center",
        },
      },
      // {
      //   id: "hyperlinks",
      //   font: {
      //     underline: "Single",
      //     color: "#358ccb",
      //   },
      // },
      {
        id: "coverSubHeader",
        interior: {
          color: "#2c3e50", // Gray background
          pattern: "Solid",
        },
        font: {
          bold: true,
          size: 16, // Font size
          color: "#ffffff", // Blue font color
        },
        border: {
          top: { style: "thin", color: "#000000" },
          // bottom: { style: 'thin', color: '#000000' },
          // // left: { style: 'thin', color: '#000000' },
          right: { style: "thin", color: "#000000" },
        },
        alignment: {
          horizontal: "Left",
          vertical: "Center",
        },
      },
      {
        id: "coverHeading",
        interior: {
          color: "#2c3e50", // Gray background
          pattern: "Solid",
        },
        font: {
          bold: true,
          size: 22, // Font size
          color: "#ffffff", // Blue font color
        },
        alignment: {
          horizontal: "Center",
          vertical: "Center",
        },
      },
      {
        id: "coverText",
        font: {
          size: 14,
        },
      },
    ];
  }, []);
  // const processRowData = (rowData) => {
  //   if (!Array.isArray(rowData)) return [];
  //   return rowData.map((row) => {
  //     return Object.fromEntries(
  //       Object.entries(row).map(([key, value]) => {
  //         // If the value is a string number, convert it to an actual number
  //         const parsedValue =
  //           typeof value === "string" && !isNaN(value) ? Number(value) : value;
  //         return [key, parsedValue];
  //       })
  //     );
  //   });
  // };
  const processRowData = (rowData) => {
  if (!Array.isArray(rowData)) return [];

  // A leading zero followed by another digit (e.g. "0171873150", "007") means
  // this is an ID/code, not a number — never coerce those, no matter which
  // field they're in. Real numeric values don't carry meaningful leading zeros.
  const isLeadingZeroCode = (value) => /^0\d/.test(value.trim());

  return rowData.map((row) => {
    return Object.fromEntries(
      Object.entries(row).map(([key, value]) => {
        const parsedValue =
          typeof value === "string" && !isNaN(value) && !isLeadingZeroCode(value)
            ? Number(value)
            : value;

        return [key, parsedValue];
      })
    );
  });
};
  const handleClientGridChange = (api, columnApi) => {
    const rowGroupCols = columnApi.getRowGroupColumns(); // tracks grouping
    const sortModel = api.getSortModel();
    const filterModel = api.getFilterModel();

    // If any grouping is applied, switch to server
    if (rowGroupCols.length > 0) {
      setUseServer(true);
    }
  };



  return (
    <div className="AgTable">
      {props.showExport || props.leftTopActions || props.rightTopActions ? (
        <div className="TableActionContainer">
          <div className="LeftContainer">{props.leftTopActions}</div>
          <div className="RightContainer">
            {props.rightTopActions}
            <div className="ActionButton">
              {props.showExport ? (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  shape="round"
                  size="small"
                  style={{
                    fontSize: 12,
                  }}
                  id="agTableExportButton"
                  onClick={exportDataToExcel}
                >
                  {props.exportLabel || "Export"}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
      <div className="ag-theme-alpine">
        <div style={{ height: "100%", position: "relative" }}>
          {loadingView ? (
            <div className="CustomLoading">
              <Loader />
            </div>
          ) : null}
          <AgGridReact
            popupParent={props.popupParent || document.body}
            domLayout={props.domLayout}
            suppressAggFuncInHeader={props.suppressAggFuncInHeader}
            singleClickEdit={props.singleClickEdit}//new----
            onCellEditingStarted={props.onCellEditingStarted}//--new
            editType={"cell"}
            getRowId={props.useRowId && getRowId}
            rowData={processRowData(props.rowData)}
            //rowData={props.rowData}
            pinnedBottomRowData={props.pinnedBottomRowData}
            columnDefs={processColumnDefs(props.columnDefs) || []}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: props.autoResize,
              headerComponentParams: {
                headerComponent: AgCustomHeader,
              },
              width: 200
            }}
            pagination={!props.footer ? false : true}
            paginationPageSize={10000}
            rowSelection="multiple"
            enableRangeSelection={true}
            enableCharts={true}
            suppressCellSelection={true}
            resizable={true}
            enablePivot={true}
            ref={gridRef}
            noRowsOverlayComponent={NoRowsOverlay}
            enableSorting={true}
            enableFilter={true}
            // statusBar={props.showStatusBar && statusBar}
            groupMultiAutoColumn={true}
            rowGroupPanelShow={props.groupPanel ? "always" : "never"}
            pivotPanelShow="always"
            onGridReady={onGridReady}
            // onGridReady={props.onGridReady} 
            grandTotalRow={props.grandTotalRow}
            pivotColumnGroupTotals="before"
            onCellValueChanged={onCellValueChanged}
            onRowValueChanged={onRowValueChanged}
            // editType={"fullRow"}
            excelStyles={excelStyles}
            onColumnVisible={handleColumnVisibilityChange}
            suppressClickEdit={!props.enableDirectEdit}
            suppressRowVirtualisation={props.disableLazy}
            defaultExcelExportParams={defaultExcelExportParams}
            gridOptions={props.defaultExpandedGroup && {
              groupDefaultExpanded: -1,
            }}
            masterDetail={props.masterDetail}
            detailCellRenderer={props.detailCellRenderer}
            detailCellRendererParams={props.detailCellRendererParams}
            detailRowHeight={props.detailRowHeight}
            detailRowAutoHeight={props.detailRowAutoHeight}
            isRowMaster={props.isRowMaster}
            keepDetailRows={props.keepDetailRows}

            autoSizeStrategy={
              props.autoResize
                ? {
                  type: "fitGridWidth",
                  defaultMinWidth: 100,
                }
                : {}
            }
            rowDragManaged={props.enableDragging}
            animateRows={props.enableDragging}
            onRowDragEnd={(event) => {
              const allRows = [];
              const rowCount = event.api.getDisplayedRowCount();

              for (let i = 0; i < rowCount; i++) {
                const rowNode = event.api.getDisplayedRowAtIndex(i);
                if (rowNode.data) {
                  allRows.push(rowNode.data);
                }
              }
              
              // Call updateNewData if provided
              if (props.updateNewData && allRows.length > 0) {
                props.updateNewData(allRows);
              }
              
              // Call onRowDragEnd prop if provided
              if (props.onRowDragEnd) {
                props.onRowDragEnd(event);
              }
            }}
            sideBar={props.hideToolbar ? {} : sideBar}
            autoGroupColumnDef={{
              headerName: "Group",
              cellRenderer: "agGroupCellRenderer",
              cellRendererParams: {
                innerRenderer: (params) => {
                  // Find the original column definition based on the field name
                  const originalColumn = props.columnDefs.find(
                    (colDef) => colDef.field === params.node.field
                  );
                  return (
                    <div
                      style={{
                        display: "flex",
                        width: "100%",
                        alignItems: "center",
                        justifyItems: "center",
                      }}
                    >
                      {handleAgCellDataFormat(
                        originalColumn?.formatType || "",
                        params.value,
                        originalColumn?.formatProps || "",
                        params.data || {}
                      )}
                      {`${props.grandTotalRow && params.value === undefined
                        ? "Total"
                        : params.value === "NA"
                          ? "-"
                          : "-"
                        }`}
                    </div>
                  );
                },
              },
            }}
        onSortChanged={() => {
              props.onSortChanged?.();
            }}
            onFilterChanged={() => {
              props.onFilterChanged?.();
            }}
            onColumnRowGroupChanged={()=>{
              props.onColumnRowGroupChanged?.();
            }}
            onColumnPivotModeChanged={()=>{
              props.onColumnPivotModeChanged?.();
            }}
            onColumnPivotChanged={()=>{
              props.onColumnPivotChanged?.();
            }}
          />
        </div>
      </div>
      {contextHolder}
    </div>
  );
});
export default memo(AgTableClient);
